import { Box, Divider, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { SchedulerFilterOptionsEnum } from '../Scheduler';
import { Event } from './Event';
import { appointmentService, blockerService, IAppointmentView, IBlockerView } from '../../../../services';
import { BlockerEvent } from './BlockerEvent';
import { computeColumnLayout } from '../utils/overlapLayout';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';

const HOUR_HEIGHT = 100;

interface IProps {
  businessId: number | undefined;
  filterOptionType: number;
  option: { id: number, name: string } | undefined;
  date: Date;
  startTime: number;
  endTime: number;
  hours: string[];
  refetch: () => any;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DayView: React.FC<IProps> = ({
  businessId,
  filterOptionType,
  option,
  date,
  startTime,
  endTime,
  hours,
  refetch,
  setIsLoading
}) => {
  const { data: appointments, isFetching: isFetchingAppointments, refetch: refetchAppointments } = useQuery(
    ['appointments', businessId, option, date],
    () => appointmentService.get(
      filterOptionType === SchedulerFilterOptionsEnum.Business ? option?.id : businessId || undefined,
      filterOptionType === SchedulerFilterOptionsEnum.Employee ? option?.id : undefined,
      date,
      date,
      true),
    { enabled: !!date && (!!businessId || !!option) }
  );

  const { data: blockers, isFetching: isFetchingBlockers, refetch: refetchBlockers } = useQuery(
    ['blockers', businessId, option, date],
    () => blockerService.get(
      filterOptionType === SchedulerFilterOptionsEnum.Business ? option?.id : businessId || undefined,
      filterOptionType === SchedulerFilterOptionsEnum.Employee ? option?.id : undefined,
      date,
      date),
    { enabled: !!date && (!!businessId || !!option) }
  );

  // Fix #3 — correct dependency array (was: [a || b] which is a single boolean)
  useEffect(() => {
    setIsLoading(isFetchingAppointments || isFetchingBlockers);
  }, [isFetchingAppointments, isFetchingBlockers]);

  const sortedAppointments = useMemo(
    () => appointments?.sort((a: IAppointmentView, b: IAppointmentView) => (a.date < b.date ? -1 : 1)) || [],
    [appointments]
  );

  const sortedBlockers = useMemo(() => {
    if (!blockers) return [];
    const dayOfWeek = new Date(date).getDay();
    return blockers
      .filter((b: IBlockerView) => !b.daysOfWeek || b.daysOfWeek.length === 0 || b.daysOfWeek.includes(dayOfWeek))
      .sort((a: IBlockerView, b: IBlockerView) => (a.startTime < b.startTime ? -1 : 1));
  }, [blockers, date]);

  // Fix #5 + #9 — unified layout for appointments AND blockers together
  const columnLayout = useMemo(() => {
    const allEvents = [
      ...sortedAppointments.map(a => ({ id: `app-${a.id}`, startTime: a.startTime, endTime: a.endTime })),
      ...sortedBlockers.map(b => ({ id: `blk-${b.id}`, startTime: b.startTime, endTime: b.endTime })),
    ];
    return computeColumnLayout(allEvents);
  }, [sortedAppointments, sortedBlockers]);

  return (
    <Box sx={{ width: '100%', display: 'flex', pb: 2, overflow: 'hidden' }}>

      {/* Sticky hours column */}
      <Box
        display='flex'
        flexDirection='column'
        alignItems='flex-start'
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 2,
          backgroundColor: 'background.paper',
          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
        }}
      >
        {hours.map((time, idx) => (
          <Box
            key={time}
            display='flex'
            justifyContent='flex-end'
            alignItems='flex-start'
            gap={1}
            style={{ width: '72px', height: HOUR_HEIGHT / 2 }}
          >
            {/* Fix #8 — offset label up so it aligns with the grid line */}
            {/* Hide the very first time label (startTime:00) to reduce visual clutter */}
            {!(idx === 0 && time.endsWith(':00')) && (
              <Typography
                variant='caption'
                sx={{
                  mt: '-9px',
                  lineHeight: 1,
                  userSelect: 'none',
                  opacity: time.endsWith(':00') ? 0.8 : 0.35,
                  fontSize: time.endsWith(':00') ? '0.72rem' : '0.62rem',
                }}
              >
                {time}
              </Typography>
            )}
            <Divider orientation='vertical' flexItem />
          </Box>
        ))}
      </Box>

      {/* Event area */}
      <Box display='flex' sx={{ flex: 1, position: 'relative', minWidth: 0 }}>

        {/* Fix #7 — horizontal grid lines */}
        {hours.map((time, i) => (
          <Box
            key={`grid-${time}`}
            sx={{
              position: 'absolute',
              top: `${i * (HOUR_HEIGHT / 2)}px`,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: time.endsWith(':00') ? 'divider' : 'rgba(128,128,128,0.12)',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Fix #6 — current time indicator */}
        {date.toDateString() === new Date().toDateString() && (
          <CurrentTimeIndicator startTime={startTime} endTime={endTime} hourHeight={HOUR_HEIGHT} />
        )}

        {sortedAppointments.map((appointment: IAppointmentView) => (
          <Event
            key={appointment.id}
            firstTime={startTime}
            currentEvent={appointment}
            columnIndex={columnLayout.get(`app-${appointment.id}`)?.columnIndex ?? 0}
            totalColumns={columnLayout.get(`app-${appointment.id}`)?.totalColumns ?? 1}
            hourHeight={HOUR_HEIGHT}
            maxLengthDescription={100}
            refetch={refetch}
            refetchAppointments={refetchAppointments}
          />
        ))}

        {sortedBlockers.map((blocker: IBlockerView) => (
          <BlockerEvent
            key={`blocker-${blocker.id}`}
            currentEvent={blocker}
            firstTime={startTime}
            hourHeight={HOUR_HEIGHT}
            maxLengthDescription={100}
            columnIndex={columnLayout.get(`blk-${blocker.id}`)?.columnIndex ?? 0}
            totalColumns={columnLayout.get(`blk-${blocker.id}`)?.totalColumns ?? 1}
            refetch={refetch}
            refetchBlockers={refetchBlockers}
          />
        ))}
      </Box>
    </Box>
  );
};